import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { DefaultFetchService } from "app/services/fetchService";
import { openHooksModalV4 } from "app/services/ModalService";
import { stateService } from "app/user-data/StateService";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { updateWindowUser } from "single-spa/bootstrap-utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRecents, IStateObject } from "app/@types/userdata";
import { allTrackers } from "app/services/track/track";

const RECENT_ENDPOINT = "/api/userdata/recent";
const ADD_RECENT_ENDPOINT = RECENT_ENDPOINT + "/add";
const UPDATE_RECENT_ENDPOINT = RECENT_ENDPOINT + "/update";
const DEFAULT_QUERY_PARAMS = { display: true };
const getQueryLimitBase = (): number => swSettings.components.Home.resources.QueryLimiter;

export const shouldReportRecentHydrationFailure = (error: unknown): boolean => {
    if (!(error instanceof Error)) {
        return true;
    }
    if (error.name === "AbortError") {
        return false;
    }
    return !(error instanceof TypeError && error.message === "Failed to fetch");
};

class RecentServiceClass {
    private static instance: RecentServiceClass;

    private recents: IRecents = [];
    private _isDirty = false;

    private constructor() {
        void window.similarwebPromises?.recentsPromise
            ?.then((res: Response) => (res?.ok ? (res.json() as Promise<IRecents>) : null))
            .then((data: IRecents | null) => {
                if (!this._isDirty) {
                    const recents = data?.filter(({ data: d }) => d) ?? [];
                    this.setRecents(recents);
                    updateWindowUser("recent", recents);
                }
            })
            .catch((err) => {
                if (!shouldReportRecentHydrationFailure(err)) {
                    return;
                }
                Sentry.errorTracker({
                    message: "[RecentService] hydration failed, using defaults",
                    e: err,
                    level: "warning",
                });
            });
    }

    static getInstance(): RecentServiceClass {
        if (!RecentServiceClass.instance) {
            RecentServiceClass.instance = new RecentServiceClass();
        }
        return RecentServiceClass.instance;
    }

    private consolidateRecent(recent: IRecents): IRecents {
        return stateService.applyDefaults(recent);
    }

    private getTodayRecents(): IRecents {
        const currentUTCDate = dayjs.utc().format("YYYY-MM-DD");
        return this.recents.filter((recent) => recent.updatedTime === currentUTCDate);
    }

    private updateRecent(newRecentObj: IRecents): void {
        this._isDirty = true;
        this.recents = this.consolidateRecent(newRecentObj);
        if (isTrialOrAfterTrial()) {
            const todayRecents = this.getTodayRecents();
            const recentEvent = new CustomEvent("todayRecentChanged", {
                detail: todayRecents.length,
            });
            window.dispatchEvent(recentEvent);
            allTrackers.dispatchTrackEvent(
                "Measure Button",
                "enriched",
                `SitesLimiterCounter/${swSettings.user.plan || "unknown" || ""}/${String(todayRecents?.length ?? 0) || ""}`,
            );
        }
    }

    private createUniqueFilter(keyGenerator: (data: IStateObject) => string) {
        const seenKeys = new Set();
        return ({ data }: { data: IStateObject }) => {
            const key = keyGenerator(data);
            if (seenKeys.has(key)) {
                return false;
            }
            seenKeys.add(key);
            return true;
        };
    }

    setRecents(recentsArgs: IRecents): void {
        this.recents = recentsArgs;
    }

    getRecents(): IRecents {
        return this.recents.filter((recent) => {
            if (recent.data.stateName) {
                return proNavigator.getState(recent.data.stateName);
            }
            return true;
        });
    }

    async addRecent(serializedState: IStateObject): Promise<void> {
        this._isDirty = true;
        const fetchService = DefaultFetchService.getInstance();
        const url = `${ADD_RECENT_ENDPOINT}?${fetchService.requestParams(DEFAULT_QUERY_PARAMS)}`;
        const newRecentObj = await fetchService.post(url, serializedState);
        this.updateRecent(newRecentObj as IRecents);
    }

    /**
     * Prevent a recent item from being promoted by resetting its visits count.
     *
     * @param recentId - The ID of the demoted recent item
     * @param permanent - True to demote it permanently, regardless of the visits count
     */
    async demoteRecent(recentId: string, permanent = false): Promise<void> {
        this._isDirty = true;
        const originItem = this.recents.find((rc) => rc.id === recentId);
        if (!originItem?.data) {
            return;
        }

        const { data, ...itemWithoutData } = originItem;
        const recentItem = {
            ...itemWithoutData,
            ...data,
            ...(permanent ? { forbidTrackerSuggestion: true } : {}),
            visits: 0,
        };

        const fetchService = DefaultFetchService.getInstance();
        const params = fetchService.requestParams({ ...DEFAULT_QUERY_PARAMS, id: recentId });
        const url = `${UPDATE_RECENT_ENDPOINT}?${params}`;
        const newRecentObj = await fetchService.put(url, recentItem);
        this.updateRecent(newRecentObj as IRecents);
    }

    uniqueFilter(isCompare: boolean) {
        const sitesKeys = new Set();
        return ({ data }: { data: IStateObject }) => {
            const { mainItem, comparedItems } = data;
            const sitesAsStringKey = mainItem + (isCompare && comparedItems?.join());
            if (sitesKeys.has(sitesAsStringKey)) {
                return false;
            }
            sitesKeys.add(sitesAsStringKey);
            return true;
        };
    }

    getRecentsWebsites(amount = 10, isCompare = false): IRecents {
        const recentsByType = this.recents
            .filter(({ data }) => data.type === "website")
            .filter(this.uniqueFilter(isCompare));
        return recentsByType.slice(0, amount);
    }

    getRecentsWebsitesAndCompanies(amount = 10): IRecents {
        const keyGenerator = ({ mainItem, params }: IStateObject) => mainItem + (params?.companyName || "");

        return this.recents
            .filter(({ data }) => data.type === "website")
            .filter(this.createUniqueFilter(keyGenerator))
            .slice(0, amount);
    }

    getRecentsWebsitesAndCompaniesByType(amount = 10): IRecents {
        const keyGenerator = ({ mainItem, type }: IStateObject) => mainItem + type;

        return this.recents
            .filter(({ data }) => data.type === "website" || data.type === "company")
            .filter(this.createUniqueFilter(keyGenerator))
            .slice(0, amount);
    }

    async addCurrentPageToRecent(): Promise<void> {
        const state = await stateService.serializeState();
        if (state && !state.hideFromRecents) {
            if (state.type === "website") {
                state.category = chosenSites.getPrimarySite()?.category;
            }
            try {
                await this.addRecent(state);
            } catch (e) {
                console.error("Could not add current page to recents", e);
            }
        }
    }

    isQueryLimitReached(): boolean {
        const todayRecents = this.getTodayRecents();
        return todayRecents.length >= getQueryLimitBase();
    }

    isQueryLimiterEnabled(): boolean {
        return !!(getQueryLimitBase() && this.isQueryLimitReached());
    }

    getQueryLimiterHook(): void {
        openHooksModalV4(proNavigator.isSalesSolution() ? "SalesSolutionQueryLimiterSIRedirect" : "QueryLimiter");
    }
}

export const RecentService = RecentServiceClass.getInstance();
